import { type FC, useState } from 'react';
import { X } from 'lucide-react';
import type { SpecialEvent } from '@/types';

interface GallerySectionProps {
  event: SpecialEvent;
}

/**
 * Gallery section displaying event images in a responsive grid
 * Includes lightbox modal for full-size viewing
 */
export const GallerySection: FC<GallerySectionProps> = ({ event }) => {
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  if (!event.galleryImages || event.galleryImages.length === 0) {
    return null;
  }

  return (
    <>
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-4xl font-viking text-turquoise-800 mb-8 text-center">
              Gallery
            </h2>

            {/* Image Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {event.galleryImages.map((imageUrl, index) => (
                <button
                  key={`${imageUrl}-${index}`}
                  onClick={() => setSelectedImage(imageUrl)}
                  className="relative aspect-[4/3] overflow-hidden rounded-lg shadow-md hover:shadow-xl transition-all group cursor-pointer"
                >
                  <img
                    src={imageUrl}
                    alt={`${event.title} gallery image ${index + 1}`}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-turquoise-900/0 group-hover:bg-turquoise-900/20 transition-colors duration-300" />
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      {selectedImage && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <button
            onClick={() => setSelectedImage(null)}
            className="absolute top-4 right-4 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
            aria-label="Close gallery"
          >
            <X className="h-6 w-6 text-white" />
          </button>
          <img
            src={selectedImage}
            alt="Gallery image fullscreen"
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </>
  );
};
